import { useState } from "react";
import { AtSign, Loader2, Lock, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const HANDLE_RE = /^[A-Za-z0-9_]{3,20}$/;

function useHandleSave(onSaved) {
  const [saving, setSaving] = useState(false);
  const save = async (raw) => {
    const handle = (raw || "").trim().replace(/^@+/, "");
    if (!HANDLE_RE.test(handle)) {
      toast.error("Handle must be 3–20 characters: letters, numbers, or underscores.");
      return false;
    }
    setSaving(true);
    try {
      const res = await api.patch("/ambassador/me/handle", { dashboard_handle: handle });
      toast.success("Public handle saved — you'll now appear by name on the leaderboard!");
      onSaved?.(res.data);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save handle");
      return false;
    } finally {
      setSaving(false);
    }
  };
  return { save, saving };
}

function HandleField({ value, onChange }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Handle</span>
      <div className="flex items-center rounded-md border border-zinc-800 bg-zinc-950 focus-within:border-[#A855F7]">
        <span className="pl-3 font-mono text-zinc-500">@</span>
        <input
          data-testid="handle-input"
          value={(value || "").replace(/^@+/, "")}
          onChange={(e) => onChange(e.target.value.replace(/^@+/, ""))}
          placeholder="neon_panda"
          maxLength={20}
          className="w-full bg-transparent px-2 py-2 font-mono text-sm text-white outline-none"
        />
      </div>
      <span className="mt-1 block text-[10px] text-zinc-600">3–20 chars · letters, numbers, underscores</span>
    </label>
  );
}

export function HandleSetupModal({ open, currentHandle, changeCount = 0, onClose, onSaved }) {
  const [value, setValue] = useState(currentHandle || "");
  const { save, saving } = useHandleSave((d) => { onSaved?.(d); onClose?.(); });
  if (!open) return null;
  const remaining = Math.max(0, 2 - changeCount);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" data-testid="handle-setup-modal">
      <div className="relative w-full max-w-md rounded-2xl border border-[#A855F7]/30 bg-zinc-950 p-6 shadow-2xl">
        <button onClick={onClose} data-testid="handle-modal-close" className="absolute right-4 top-4 text-zinc-500 hover:text-white">
          <X className="h-5 w-5" />
        </button>
        <div className="inline-flex items-center gap-2 rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#A855F7]">
          <Sparkles className="h-3.5 w-3.5" /> Get featured
        </div>
        <h2 className="mt-3 font-display text-2xl font-black text-white">Pick your public handle</h2>
        <p className="mt-2 text-sm text-zinc-400">
          This is the alias shown next to your rank on the public leaderboard.{" "}
          <strong className="text-zinc-200">Until you set it, you won&apos;t appear on the public leaderboard at all.</strong>
        </p>
        <div className="mt-4">
          <HandleField value={value} onChange={setValue} />
        </div>
        <p className="mt-2 text-[11px] text-amber-300/80">
          Heads up: you can set / change this only <strong>{remaining}</strong> more time{remaining === 1 ? "" : "s"}. After that, contact us to change it.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} data-testid="handle-modal-skip" className="rounded-md border border-zinc-800 px-4 py-2 text-sm text-zinc-300 hover:border-zinc-700">
            Maybe later
          </button>
          <button
            onClick={() => save(value)}
            disabled={saving || !value}
            data-testid="handle-modal-save"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <AtSign className="h-4 w-4" />} Save handle
          </button>
        </div>
      </div>
    </div>
  );
}

export function HandleBanner({ onOpen }) {
  return (
    <div
      data-testid="handle-banner"
      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#A855F7]/30 bg-gradient-to-r from-[#A855F7]/10 to-transparent p-4"
    >
      <div className="flex items-center gap-3">
        <AtSign className="h-5 w-5 shrink-0 text-[#A855F7]" />
        <div>
          <p className="text-sm font-bold text-white">You&apos;re hidden from the public leaderboard</p>
          <p className="text-xs text-zinc-400">Set a public handle to appear on the public leaderboard.</p>
        </div>
      </div>
      <button
        onClick={onOpen}
        data-testid="handle-banner-cta"
        className="rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white hover:-translate-y-0.5 transition-transform"
      >
        Set my handle
      </button>
    </div>
  );
}

export function HandleCard({ handle, changeCount = 0, onSaved }) {
  const [value, setValue] = useState(handle || "");
  const { save, saving } = useHandleSave(onSaved);
  const remaining = Math.max(0, 2 - changeCount);
  const locked = remaining <= 0;
  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6 lg:col-span-2" data-testid="handle-card">
      <div className="flex items-start gap-2">
        <AtSign className="mt-0.5 h-4 w-4 text-[#A855F7]" />
        <div className="flex-1">
          <h3 className="font-display text-xl font-bold">Public handle</h3>
          <p className="mt-1 text-xs text-zinc-500">
            Shown on the public leaderboard next to your rank.{" "}
            {locked
              ? "You've used all your changes — email info@noobsync.com to update it."
              : `You can set / change it ${remaining} more time${remaining === 1 ? "" : "s"}.`}
          </p>
        </div>
      </div>
      {locked ? (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/70 px-3 py-2.5">
          <Lock className="h-3.5 w-3.5 text-zinc-500" />
          <span className="font-mono text-sm text-zinc-200">@{handle || "—"}</span>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <HandleField value={value} onChange={setValue} />
          </div>
          <button
            onClick={() => save(value)}
            disabled={saving || !value}
            data-testid="handle-card-save"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <AtSign className="h-4 w-4" />} Save handle
          </button>
        </div>
      )}
    </div>
  );
}
